import {
  Box,
  DatePicker,
  Grid,
  Typography,
  TextField,
} from '@atgfr8/atg-ui-tailwind';
import { Button, Notification, SelectInput } from '@components';
import { createForm } from '@felte/solid';
import { useParams } from '@solidjs/router';
import { createEdiRequest } from '@store/customers/customerDetails';
import { formatDataTestId } from '@utils/dataTestIdFormatter';
import { priorityList } from '@views/customer/customerDetail/constants';
import { Accessor, Show } from 'solid-js';

type SetupEdiProps = {
  modalId: string;
  handleClose?: () => void;
  ediMode?: Accessor<string>;
  setEdiMode?: (value: string) => void;
};

type EdiSetupModalFormStateType = {
  details: string;
  dueDate: string;
  comment: string;
  priority: string;
  objectId: string;
};

const SetupEdi = (props: SetupEdiProps) => {
  const params = useParams();
  const { form, reset, data, setFields, isSubmitting, setIsSubmitting } =
    createForm<EdiSetupModalFormStateType>({
      initialValues: {
        details: '',
        dueDate: '',
        comment: '',
        priority: '1',
        objectId: params.id,
      },
      onSubmit: async (values) => {
        await handleSubmission(values);
      },
    });

  const handleSubmission = async (values: EdiSetupModalFormStateType) => {
    try {
      setIsSubmitting(true);
      const formPayload = Object.fromEntries(
        Object.entries(values).filter(([_, value]) => value !== ''),
      );

      const res = await createEdiRequest({
        ...formPayload,
        priority: data().priority,
      });
      if (res.success) {
        reset();
        props.setEdiMode?.('2');
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      throw new Error(`Failed to SetUp Edi: ${errorMessage}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box>
      <Show when={props.ediMode?.() === '0'}>
        <Box>
          <Typography class="text-xl! font-medium!">
            Armstrong is now EDI compliant.
          </Typography>
          <Typography class="text-base!">
            If you wish to set up your customer with EDI, there will be a{' '}
            <span class="text-green-800 font-semibold!">$500</span> setup fee.
            You will be charged once we begin work on the customer.
          </Typography>
          <Typography class="text-base!">
            The process of integrating with a customer is arduous and
            time-consuming and could take 2-6 weeks to complete before you can
            move a load through the system.
          </Typography>
          <Box class="flex justify-end items-center">
            <Button
              class="my-4!"
              variant="contained"
              label="I understand"
              onClick={() => props.setEdiMode?.('1')}
              dataTestId={formatDataTestId('I understand', 'button')}
            />
          </Box>
        </Box>
      </Show>
      <Show when={props.ediMode?.() === '1'}>
        <form ref={form}>
          <Box
            style={{
              'flex-grow': 1,
            }}
            pt={1}
          >
            <Grid container spacing={2}>
              <Grid item size={12}>
                <Grid container spacing={2}>
                  <Grid item size={12}>
                    <Typography class="text-sm! font-medium!">
                      Who do we need to reach out to
                    </Typography>

                    <TextField
                      label=""
                      placeholder="Enter an Email Address"
                      value={data().details}
                      onChange={(e: string) => {
                        setFields('details', e);
                      }}
                      dataTestId="setup-edi-details-textInput"
                    />
                    <Show when={data().details === ''}>
                      <Notification
                        type="warning"
                        text="EDI Contact is required. We must have a contact at the customer to reach out to"
                      />
                    </Show>
                    <Notification
                      type="info"
                      text="This is a contact to customer for us to reach out to in order to begin the process. Do not put your own email address. We cannot start without this."
                    />
                  </Grid>
                  <Grid item size={12}>
                    <Typography class="text-sm! font-medium!">
                      Deadline/Go Live Date
                    </Typography>
                    <DatePicker
                      handleChange={(value) => {
                        setFields('dueDate', value);
                      }}
                      value={data().dueDate}
                      label=""
                      placeholder="MM/DD/YYYY"
                      dataTestId="setup-edi-due-date-datepicker"
                    />
                    <Notification
                      type="info"
                      text="EDI setups can range from hours to weeks"
                    />
                  </Grid>
                  <Grid item size={12}>
                    <Typography class="text-sm! font-medium!">Note</Typography>
                    <TextField
                      multiline
                      rows={5}
                      value={data().comment}
                      onChange={(itm: string) => setFields('comment', itm)}
                      dataTestId="setup-edi-comment-textAreaField"
                    />
                  </Grid>
                  <Grid item size={12}>
                    <Typography class="text-sm! font-medium!">
                      Priority (please be truthful and fair, this helps us plan)
                    </Typography>
                    <SelectInput
                      label=""
                      placeholder="Select Unit Type"
                      menuItems={priorityList.map((p) => ({
                        label: p.label,
                        value: String(p.value),
                      }))}
                      value={data().priority}
                      onChange={(val) => setFields('priority', val)}
                      dataTestId={formatDataTestId(
                        'Setup EDI priority',
                        'selectInput',
                      )}
                    />
                    <Show when={data().priority === '0'}>
                      <Notification
                        type="info"
                        text="EDI would be a nice to have, but I can still move freight without it."
                      />
                    </Show>
                    <Show when={data().priority === '1'}>
                      <Notification
                        type="info"
                        text="e.g. I can significantly increase my revenue and productivity if I can use EDI"
                      />
                    </Show>
                    <Show when={data().priority === '3'}>
                      <Notification
                        type="warning"
                        text="e.g.: It is costing me money not to have EDI integration with this customer."
                      />
                    </Show>
                    <Show
                      when={
                        !(
                          data().priority === '0' ||
                          data().priority === '1' ||
                          data().priority === '3'
                        )
                      }
                    >
                      <Notification
                        type="error"
                        text="e.g.: I will lose my largest customer in the very near term if this does not happen immediately!"
                      />
                    </Show>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item size={12} justifyContent="end" display="flex">
                <Button
                  label="next"
                  type="submit"
                  variant="contained"
                  color="primary"
                  isLoading={isSubmitting()}
                  disabled={isSubmitting()}
                  dataTestId={formatDataTestId('next', 'button')}
                />
              </Grid>
            </Grid>
          </Box>
        </form>
      </Show>
      <Show when={!(props.ediMode?.() === '0' || props.ediMode?.() === '1')}>
        <Box class="bg-[#dff0d8] p-[15px] rounded-sm">
          <Typography class="text-[#3c763d] text-lg! font-semibold!">
            Your Request has been added to the queue
          </Typography>
          <Typography class="text-base! font-medium! text-[#3c763d]!">
            Please give us some time to work with this, this is a difficult and
            time-consuming process. If you don’t hear back from us for a while,
            do not panic. You are in the queue, and your request will be
            addressed based on priority, date, and workload. We will reach out
            to you when there are actionable items. Please email any questions
            and concerns to{' '}
            <a href="mailto:edi@armstrongtransport.com" class="">
              <Typography
                component="span"
                class="text-[#ff0000]! font-semibold!"
              >
                edi@armstrongtransport.com
              </Typography>
            </a>
          </Typography>
          <Typography class="text-base! font-semibold! text-[#3c763d]!">
            Please be sure to upload any EDI related documents from the customer
            you have received. This is the first place we will look.
          </Typography>
          <Box class="flex justify-end items-center">
            <Button
              class="my-4!"
              variant="contained"
              label="Close"
              onClick={() => props.handleClose?.()}
              dataTestId={formatDataTestId('Close', 'button')}
            />
          </Box>
        </Box>
      </Show>
    </Box>
  );
};

export default SetupEdi;
